import type { AdminInspectorListItemDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { InspectorsFilterBar } from './_components/InspectorsFilterBar';
import { buildInspectorColumns } from './_lib/inspectorColumns';
import { hasActiveFilters, inspectorFiltersSchema } from './_lib/inspectorFilters';
import { InspectorDetailDrawer } from './InspectorDetailDrawer';
import { useAdminInspectorsList } from '../../api/admin-inspectors';
import { RequirePermission } from '../../auth';
import { AdminDataGrid } from '../../components/data-grid';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useUrlFilters } from '../../hooks/useUrlFilters';

/**
 * `/inspectors` — inspectors moderation list (AUT-125 / ADMIN-13).
 *
 * Gated on `verify_inspectors` (the controller-level permission). PII
 * columns (phone) render via `<RedactedField perm="view_pii">`, which
 * agrees with the server's `piiRevealed` flag (both derive from the same
 * backend permission post-substep-0).
 *
 * Filters + pagination are URL-state-driven via `useUrlFilters`; cursor
 * pagination bridges the cursor backend to the page-based DataGrid via
 * `useCursorPagination`. Row click opens the 720px detail drawer.
 */
const InspectorsListPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = useUrlFilters(inspectorFiltersSchema);
  const pager = useCursorPagination();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Any filter change resets pagination to the first page (the cursor
  // space is only valid for the filter set that produced it).
  const filterKey = `${(filters.status ?? []).join(',')}|${filters.cityCountry ?? ''}|${filters.city ?? ''}|${filters.q ?? ''}`;
  useEffect(() => {
    pager.reset();
    // `pager.reset` is stable (useCallback); re-run only on filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const query = useAdminInspectorsList({
    ...(filters.status && filters.status.length > 0 ? { status: filters.status } : {}),
    ...(filters.cityCountry ? { cityCountry: filters.cityCountry } : {}),
    ...(filters.city ? { city: filters.city } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    cursor: pager.cursor,
    limit: pager.pageSize,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  // Feed the latest nextCursor into the pager once each fetch settles.
  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  const columns = useMemo(() => buildInspectorColumns(t), [t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('inspectors.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('inspectors.subtitle')}
        </Typography>
      </Box>

      <InspectorsFilterBar
        filters={filters}
        setFilters={setFilters}
        canClear={hasActiveFilters(filters)}
        onClear={reset}
      />

      <AdminDataGrid<AdminInspectorListItemDto>
        rows={items}
        columns={columns}
        rowCount={pager.rowCount(items.length)}
        loading={query.isLoading || query.isFetching}
        error={query.error}
        paginationModel={pager.paginationModel}
        onPaginationModelChange={pager.onPaginationModelChange}
        sortModel={[]}
        onSortModelChange={() => undefined}
        onRowClick={(row) => setSelectedId(row.id)}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            {t('inspectors.empty')}
          </Typography>
        }
      />

      <InspectorDetailDrawer inspectorId={selectedId} onClose={() => setSelectedId(null)} />
    </Box>
  );
};

const Observed = observer(InspectorsListPageInner);

/** Whole-page gate: no `verify_inspectors` → the default 403 panel. */
export const InspectorsListPage = (): ReactElement => (
  <RequirePermission perm="verify_inspectors">
    <Observed />
  </RequirePermission>
);
